import React from "react";

/**
 * Utility function for filtering non-coverage identifiers.
 *
 * @param {Array} identifiers - An array of identifier objects.
 * @returns {Array} - Returns an array of identifiers that are not coverage identifiers.
 */
export const patientIdentifiers = (identifiers) => {
  return identifiers.filter((i) => {
    if (i && Object.keys(i).length === 1 && i.hasOwnProperty("extension")) {
      const overrideIdentifier = i.extension[0].valueIdentifier;
      return !(
        "type" in overrideIdentifier &&
        "coding" in overrideIdentifier.type &&
        overrideIdentifier.type.coding.length > 0 &&
        overrideIdentifier.type.coding[0].code === "COV"
      );
    }
    return !(
      (i.hasOwnProperty("extension") &&
        i.extension.every(
          (ext) =>
            ext.url === "additionalDataFromRecords" &&
            ext.valueBoolean === true,
        )) ||
      ("type" in i &&
        "coding" in i.type &&
        i.type.coding.length > 0 &&
        i.type.coding[0].code === "COV")
    );
  });
};

/**
 * Utility function for filtering coverage identifiers.
 *
 * @param {Array} identifiers - An array of identifier objects.
 * @returns {Array} - Returns an array of identifiers that are coverage identifiers.
 */
export const patientCoverages = (identifiers) => {
  return identifiers.filter((i) => {
    if (i && Object.keys(i).length === 1 && i.hasOwnProperty("extension")) {
      const overrideIdentifier = i.extension[0].valueIdentifier;
      return (
        "type" in overrideIdentifier &&
        "coding" in overrideIdentifier.type &&
        overrideIdentifier.type.coding.some((c) => c.code === "COV")
      );
    }
    return (
      !(
        i.hasOwnProperty("extension") &&
        i.extension.every(
          (ext) =>
            ext.url === "additionalDataFromRecords" &&
            ext.valueBoolean === true,
        )
      ) &&
      "type" in i &&
      "coding" in i.type &&
      i.type.coding.some((c) => c.code === "COV")
    );
  });
};

/**
 * Utility function for filtering out additionalDemographics from demographics.
 *
 * @param {Array} demographics - An array of demographic objects.
 * @returns {Array} - Returns an array of demographic objects without the additionalDemographics.
 */
export const removeAdditionalDemographics = (demographics) => {
  return (
    demographics &&
    demographics.filter(
      (attr) =>
        !(
          attr.extension &&
          attr.extension.every(
            (ext) =>
              ext.url === "additionalDataFromRecords" &&
              ext.valueBoolean === true,
          )
        ),
    )
  );
};

/**
 * Helper function to render field values with diff highlighting.
 * Shows prior (red) and new (green) values when they differ in preview mode.
 *
 * @param {any} currentValue - The current/new value to display
 * @param {any} priorValue - The prior/old value to compare against
 * @param {string} changeType - The type of change: 'added', 'removed', 'changed', or null
 * @param {boolean} isPreviewMode - Whether in preview mode
 * @param {boolean} hasDiff - Whether there's a difference in the parent object
 * @returns {JSX.Element} - Returns a div with appropriate diff highlighting
 */
export const renderFieldWithDiff = (
  currentValue,
  priorValue,
  changeType,
  isPreviewMode,
  hasDiff,
) => {
  if (!isPreviewMode || !hasDiff || !changeType) {
    return <div>{currentValue}</div>;
  }

  // Safely stringify values for comparison, handling React elements and arrays
  const safeStringify = (val) => {
    if (val == null) return "";
    if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    )
      return String(val);
    if (React.isValidElement(val)) {
      // Extract text content from React elements for comparison
      const children = val.props?.children;
      if (children != null) return safeStringify(children);
      return "";
    }
    if (Array.isArray(val)) return val.map(safeStringify).join(",");
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  };

  const currentStr = safeStringify(currentValue);
  const priorStr = safeStringify(priorValue);

  // If values are the same, just show current value without highlighting
  if (currentStr === priorStr) {
    return <div>{currentValue}</div>;
  }

  if (changeType === "added" || !priorValue || priorValue === "-") {
    return (
      <div>
        <span className="preview-diff-highlight">{currentValue}</span>
      </div>
    );
  }

  if (changeType === "removed" || !currentValue || currentValue === "-") {
    return (
      <div>
        <span className="preview-diff-prior">{priorValue}</span>
      </div>
    );
  }

  if (changeType === "changed") {
    return (
      <div>
        <span className="preview-diff-prior">{priorValue}</span>
        {" → "}
        <span className="preview-diff-highlight">{currentValue}</span>
      </div>
    );
  }

  return <div>{currentValue}</div>;
};
